import { useMemo } from 'react';
import { useAppSelector } from '@/core/global-store';
import { BASELINE_ROLE_PERMISSIONS } from '@/features/accessControl/data/baselinePermissions';

function collectPermissionNames(me: Record<string, unknown> | null): string[] {
    const names = new Set<string>();

    const flat = me?.permissions;
    if (Array.isArray(flat)) {
        for (const p of flat as Array<{ name?: string }>) {
            if (p?.name) names.add(p.name);
        }
    }
    const allPerm = me?.all_permissions;
    if (Array.isArray(allPerm)) {
        for (const p of allPerm as Array<{ name?: string } | string>) {
            if (typeof p === 'string') names.add(p);
            else if (p && typeof p === 'object' && 'name' in p && (p as { name?: string }).name) {
                names.add((p as { name: string }).name);
            }
        }
    }

    if (me?.roles && Array.isArray(me.roles)) {
        for (const r of me.roles as Array<{ permissions?: Array<{ name?: string }> }>) {
            for (const p of r.permissions ?? []) {
                if (p?.name) names.add(p.name);
            }
        }
    }

    return [...names, ...BASELINE_ROLE_PERMISSIONS.filter((name) => !names.has(name))];
}

/** All permission name strings for the logged-in user (union across roles). */
export function usePermissionNames(): string[] {
    const me = useAppSelector((s) => s.auth.me) as Record<string, unknown> | null;
    return useMemo(() => collectPermissionNames(me), [me]);
}

export function useHasPermission(permission: string): boolean {
    const names = usePermissionNames();
    return names.includes(permission);
}

export function useHasAnyPermission(...permissions: string[]): boolean {
    const names = usePermissionNames();
    return permissions.some((p) => names.includes(p));
}
